'use client'

import { useState, useRef, useLayoutEffect, useEffect } from 'react'
import { Card } from '@acko/card'
import { Separator } from '@acko/separator'
import { Typography } from '@acko/typography'

export type TOCItem = {
  id: string
  label: string
}

export type TableOfContentsProps = {
  items: TOCItem[]
  title?: string
  activeId?: string
  stickyTopOffset?: number
}

const TOC_BOTTOM_GUTTER = 32

export const TableOfContents = ({
  items,
  title = 'In this article',
  activeId,
  stickyTopOffset = 32,
}: TableOfContentsProps) => {
  const [activeItemId, setActiveItemId] = useState<string | undefined>(activeId ?? items[0]?.id)
  const [barStyle, setBarStyle] = useState<{ top: number; height: number } | null>(null)
  const navRef = useRef<HTMLElement>(null)
  const maxHeight = `calc(100vh - ${stickyTopOffset + TOC_BOTTOM_GUTTER}px)`

  useEffect(() => {
    const handleScroll = () => {
      const visibleItem =
        items
          .map(item => {
            const element = document.getElementById(item.id)
            if (!element) return null

            const elementTop = element.offsetTop - stickyTopOffset
            const elementBottom = elementTop + element.offsetHeight
            const scrollPosition = window.scrollY

            if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
              return item
            }

            return null
          })
          .find((item): item is TOCItem => item !== null) ?? null

      if (visibleItem) {
        setActiveItemId(visibleItem.id)
      }
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [items, stickyTopOffset])

  // Measure the active item's position inside the nav and slide the bar there.
  // useLayoutEffect runs synchronously after DOM paint → no flash on initial render.
  useLayoutEffect(() => {
    if (!navRef.current || !activeItemId) return
    const activeLink = navRef.current.querySelector<HTMLElement>(`[data-toc-id="${activeItemId}"]`)
    if (!activeLink) return
    const linkTop = activeLink.offsetTop
    const linkBottom = linkTop + activeLink.offsetHeight
    const visibleTop = navRef.current.scrollTop
    const visibleBottom = visibleTop + navRef.current.clientHeight

    if (linkTop < visibleTop) {
      navRef.current.scrollTop = linkTop
    } else if (linkBottom > visibleBottom) {
      navRef.current.scrollTop = linkBottom - navRef.current.clientHeight
    }

    setBarStyle({
      top: linkTop,
      height: activeLink.offsetHeight,
    })
  }, [activeItemId])

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const targetElement = document.getElementById(id)
    if (!targetElement) return

    window.scrollTo({
      top: targetElement.getBoundingClientRect().top + window.scrollY - stickyTopOffset,
      behavior: 'smooth',
    })
  }

  return (
    // overflow-hidden clips the bar inside the card's rounded corners
    <Card variant="secondary" style={{ maxHeight }} className="w-full flex flex-col overflow-hidden!">
      {/* Title */}
      <div className="px-[16px] pt-[16px] pb-[14px] @[768px]:px-[20px] @[768px]:pt-[20px] @[768px]:pb-[16px]">
        <Typography scale="lg" as="h3" emphasis="bold">
          {title}
        </Typography>
      </div>

      <Separator decorative />

      {/* nav is the positioning context for the sliding bar */}
      <nav ref={navRef} aria-label={title} className="relative min-h-0 flex-1 overflow-y-auto scrollbar-none">
        {/* Single sliding bar — animates top + height */}
        {barStyle && (
          <div
            aria-hidden="true"
            className="absolute left-0 w-[4px] [background-color:var(--color-primary)] transition-all duration-300 ease-in-out"
            style={{ top: barStyle.top, height: barStyle.height }}
          />
        )}

        <ul>
          {items.map((item, idx) => {
            const isActive = item.id === activeItemId
            const isLast = idx === items.length - 1

            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  data-toc-id={item.id}
                  onClick={e => handleClick(e, item.id)}
                  className={[
                    'flex items-center py-[12px] pl-[16px] pr-[16px] @[768px]:pl-[20px] @[768px]:pr-[20px]',
                    '[color:var(--color-text-default)] hover:[color:var(--color-primary)]',
                    'transition-colors duration-200',
                    'focus:outline-none focus-visible:ring-2 [--tw-ring-color:var(--color-primary)]',
                  ].join(' ')}>
                  <span
                    className={[
                      '[font-size:var(--font-body-sm-size)] [line-height:var(--font-body-sm-line)]',
                      isActive
                        ? '[font-weight:var(--font-label-lg-weight)]'
                        : '[font-weight:var(--font-body-md-weight)]',
                    ].join(' ')}>
                    {item.label}
                  </span>
                </a>
                {!isLast && <Separator decorative />}
              </li>
            )
          })}
        </ul>
      </nav>
    </Card>
  )
}
